import React, { useState, useEffect } from 'react';
import { db } from '../../services/firebase';
import { collection, getDocs, doc, updateDoc, query, orderBy } from 'firebase/firestore';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';

const UserManagement = () => {
  const { currentUser } = useAuth();
  const { createNotification } = useNotifications();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingUsers, setUpdatingUsers] = useState({});

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Obtener todos los usuarios de Firestore
      const usersRef = collection(db, 'users');
      const q = query(usersRef, orderBy('email'));
      const snapshot = await getDocs(q);
      
      const loadedUsers = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        isAdmin: doc.data().role === 'admin' || doc.data().isAdmin === true || 
                (doc.data().permissions && doc.data().permissions.admin === true)
      }));
      
      setUsers(loadedUsers);
      setLoading(false);
    } catch (err) {
      console.error('Error cargando usuarios:', err);
      setError('Error cargando la lista de usuarios: ' + err.message);
      setLoading(false);
    }
  };

  const toggleAdminStatus = async (userId, currentAdminStatus) => {
    if (userId === currentUser.uid) {
      alert('No puedes cambiar tu propio rol de administrador');
      return;
    }
    
    try {
      setUpdatingUsers(prev => ({ ...prev, [userId]: true }));
      
      // Actualizar el usuario en Firestore
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role: currentAdminStatus ? 'user' : 'admin',
        isAdmin: !currentAdminStatus
      });
      
      // Actualizar la lista local
      setUsers(users.map(user => 
        user.id === userId ? { ...user, isAdmin: !currentAdminStatus } : user
      ));
      
      // Enviar notificación al usuario
      try {
        await createNotification({
          userId: userId,
          title: currentAdminStatus ? 'Rol de administrador removido' : 'Rol de administrador asignado',
          message: currentAdminStatus 
            ? 'Tu rol de administrador ha sido removido por el administrador principal' 
            : 'Has sido asignado como administrador. Ahora recibirás todas las notificaciones de citas',
          icon: currentAdminStatus ? '🔄' : '🛡️',
          category: 'system',
          priority: 'high',
          actionUrl: '/'
        });
      } catch (notifError) {
        console.error('Error enviando notificación:', notifError);
      }
      
      // También notificar al administrador actual
      await createNotification({
        userId: currentUser.uid,
        title: 'Gestión de administradores',
        message: `Has ${currentAdminStatus ? 'eliminado' : 'asignado'} el rol de administrador a ${users.find(u => u.id === userId)?.email || userId}`,
        icon: '👥',
        category: 'system',
        priority: 'medium',
        actionUrl: '/admin/users'
      });
      
    } catch (err) {
      console.error('Error actualizando rol de usuario:', err);
      alert('Error: ' + err.message);
    } finally {
      setUpdatingUsers(prev => ({ ...prev, [userId]: false }));
    }
  };

  const filteredUsers = searchTerm 
    ? users.filter(user => 
        (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.displayName && user.displayName.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : users;

  return (
    <Layout>
      <div style={{ padding: '1rem 2rem', maxWidth: '100%' }}>
        <h1 style={{ marginBottom: '1.5rem' }}>Gestión de Administradores</h1>
        
        <div style={{ marginBottom: '1rem' }}>
          <p>
            Los usuarios marcados como <strong>administradores</strong> recibirán notificaciones 
            cuando los clientes creen o cancelen citas.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', marginTop: '1rem' }}>
            <input 
              type="text"
              placeholder="Buscar usuarios..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                width: '100%',
                maxWidth: '400px'
              }}
            />
            <button 
              onClick={loadUsers}
              style={{
                marginLeft: '1rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              🔄 Actualizar
            </button>
          </div>
        </div>
        
        {loading ? (
          <p>Cargando usuarios...</p>
        ) : error ? (
          <p style={{ color: 'red' }}>{error}</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
              <thead>
                <tr>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Usuario</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Email</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>Rol</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ padding: '1rem', textAlign: 'center' }}>
                      No se encontraron usuarios
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map(user => (
                    <tr key={user.id}>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #f0f0f0' }}>
                        {user.displayName || 'Sin nombre'}
                        {user.id === currentUser.uid && (
                          <span style={{ 
                            marginLeft: '0.5rem',
                            fontSize: '0.8rem',
                            background: '#e0f2fe',
                            padding: '0.2rem 0.4rem',
                            borderRadius: '4px'
                          }}>
                            (Tú)
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #f0f0f0' }}>{user.email || 'Sin email'}</td>
                      <td style={{ 
                        padding: '0.75rem', 
                        borderBottom: '1px solid #f0f0f0',
                        textAlign: 'center'
                      }}>
                        <span style={{ 
                          background: user.isAdmin ? '#dcfce7' : '#f3f4f6',
                          color: user.isAdmin ? '#166534' : '#4b5563',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '999px',
                          fontSize: '0.875rem',
                          fontWeight: 500
                        }}>
                          {user.isAdmin ? 'Admin' : 'Usuario'}
                        </span>
                      </td>
                      <td style={{ 
                        padding: '0.75rem', 
                        borderBottom: '1px solid #f0f0f0',
                        textAlign: 'center'
                      }}>
                        <button
                          onClick={() => toggleAdminStatus(user.id, user.isAdmin)}
                          disabled={updatingUsers[user.id] || user.id === currentUser.uid}
                          style={{
                            padding: '0.4rem 0.75rem',
                            background: user.isAdmin ? '#fee2e2' : '#dcfce7',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: user.id === currentUser.uid ? 'not-allowed' : 'pointer',
                            opacity: user.id === currentUser.uid ? 0.5 : 1
                          }}
                        >
                          {updatingUsers[user.id] ? '...' : user.isAdmin ? 'Quitar Admin' : 'Hacer Admin'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        
        {users.length > 0 && (
          <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
            <p>Total: {users.length} usuarios • Administradores: {users.filter(u => u.isAdmin).length}</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UserManagement;
